import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form } from 'antd';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import { useMethods } from '@lhb/hook';
import { isArray, isNotEmptyAny } from '@lhb/func';
import {
  calculateHandle,
  ControlType,
  echoEstimateSalesComYHT,
  echoEstimateSalesConclusionComYHT,
  echoPassengerFlowCom,
  formDataComsToTiled,
  formDataEcho,
  getCalculateComs,
  getEditDefaultValuesByTiledFormData,
  getEditValuesByTiledFormData,
  hasAreaProtectionAndAddress,
  parseValueCatch,
  relevancyShow,
} from '@/common/components/business/DynamicComponent/config';
import { pcdIdentification, addressIdentification, } from '@/common/components/business/DynamicComponent/customize';
import cs from 'classnames';
import styles from './index.module.less';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Container from '@/common/components/Data/V2Container';
import V2Tabs from '@/common/components/Data/V2Tabs';
import Main from './Main';
import moment from 'moment';
import { radioEnum } from '@/common/components/business/DynamicComponent/components/Footprint';
import { getFormDetail, getTemplateDetail } from '@/common/api/expandStore/chancepoint';
import ClusterHintModal from './ClusterHintModal';
import { useBigdataFn } from '@/common/utils/bigdata';
import { handleJNPValue } from '@/common/components/business/DynamicComponent/components/CalculationJNP/components/transfer2form';
import Header from './components/Header';
import Footer from './components/Footer';
import { ApprovalTypeValue } from '../ts-config';
import { useSelector } from 'react-redux';
import { applyYiMingChanceFieldRules } from '@/common/components/business/DynamicComponent/yiMingChancepoint';
import { applyLocxxMockTemplateConfig, createMockLocxxTemplate, isLocxxOpportunityMockEnabled } from './locxxOpportunity';
import { applyFranchiseePrefill, getFranchiseeAddress } from './franchiseePrefill';
import { initializeFranchiseeAddress } from './franchiseeAddressInitialization';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

/**
 * 创建/机会点时的表单
 * @Description 逻辑说明：
 * 1. 利用antd的Form组件的onValuesChange监听输入的变化
 * 2. 从接口获取到的组件数据，通过递归将所有组件平铺到一个对象数组中
 * 3. 根据每个表单项绑定的name（即identification），在平铺的组件对象中查找对应的组件，然后进行textValue的赋值
 */
const ChanceCreateDrawer: FC<{
  chancepointAlias?,
  /** 表单抽屉所需数据 */
  drawerData: {
    open,
    /** 模板id */
    templateId,
    /** 编辑时的id */
    id,
    /** 权限，目前看只有鱼你项目会传：src/common/components/business/Fishtogether/PointDetail/components/Detail/Header.tsx */
    importPermission?,
    /** 拓店任务相关信息 */
    expandStoreData?,
    /** 机会点点位评估审批流ID */
    evaluationFlowId?,
    /** 从机会点详情进入编辑时传入的区域保护特殊申请状态。 */
    antiCollisionApprovalStatus?,
  },
  closeHandle,
  onSearch?,
  handleCreateSuccessed?,
  handleEditSuccessed,
  assignedData?,
  /** 仅加盟商带址新建时传入的已授权详情；不作为任务关联参数。 */
  franchiseeSource?: any,
  /** 创建后需要与机会点关联 */
  needAssiocateChance?,
  /** 创建时用于查询商圈默认资源库项目的拓店任务 ID */
  taskId?: number,
  /** 商圈城市、名称，仅用于项目列表默认筛选。 */
  projectSearchDefaults?: { city?: (number | null | undefined)[]; projectName?: string | null; longitude?: number | string | null; latitude?: number | string | null },
  /** 拓店任务id，以后可能会用到 */
  // expansionTaskId?,
  /** 拓店任务商圈id */
  expansionTaskClusterId?,
  /** 是否拓店任务-点位评估进入 */
  isExpandStoreTaskIn?: boolean,
  /** 是否 Locxx 拓店任务机会点创建/编辑。 */
  isLocxxTask?: boolean,
  /** Locxx 新建保存后关联到当前任务。 */
  handleLocxxSave?: (chancePointId: number) => Promise<void> | void,
  /** 拓店任务-提交点位评估 */
  handleExpandStoreApproved?,
}> = ({
  chancepointAlias,
  drawerData,
  closeHandle,
  onSearch = () => {},
  handleCreateSuccessed,
  handleEditSuccessed,
  assignedData,
  franchiseeSource,
  needAssiocateChance, // 创建后需要与机会点关联
  taskId,
  projectSearchDefaults,
  // expansionTaskId,
  expansionTaskClusterId,
  isExpandStoreTaskIn,
  isLocxxTask = false,
  handleLocxxSave,
  handleExpandStoreApproved
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const isYiMing = !!useSelector((state: any) => state.common.tenantCheck?.isYiMing);

  /*
    是否传统的点位评估编辑页（不包含一鸣等租户走机会点点位评估审批流的场景），是的话字段参照拓店模板设置，而不是流程配置
    请求formDetail后，按照有无绑定taskId来决定，有则true
    默认值必须是false，新建机会点时不请求formDetail，会使用默认值
  */
  const [isTraditionEvaluation, setIsTraditionEvaluation] = useState<boolean>(false);

  const tiledFormDataRef = useRef<any[]>([]); // 所有组件字段平铺后的对象数组；如果内部包含了子表单，编辑回显的时候，子表单的 textValue 会在 SubForm 组件内通过 valueChange 触发更新一次
  const saveSucceedRef = useRef(false); // 已保存成功
  const topRef: any = useRef(); // 吸顶部分
  const tabActiveRef: any = useRef();
  const tabContentRefs: any = useRef([]);
  const [form] = Form.useForm();
  const { open, templateId, id, importPermission } = drawerData;
  const [formData, setFormData] = useState<any>({}); // 接口返回的动态表单源数据
  const resourceProjectFormData = useMemo(() => ({
    ...formData,
    taskEntry: isLocxxTask,
    resourceProjectTaskId: id ? formData.resourceProjectTaskId : taskId,
    resourceProjectSearchDefaults: projectSearchDefaults,
  }), [formData, id, taskId, projectSearchDefaults, isLocxxTask]);
  const [tabActive, setTabActive] = useState<string>(''); // 当前tab值
  // supportDirectApproval 是否支持直接提交审批 1:支持 2:不支持
  const [supportDirectApproval, setSupportDirectApproval] = useState<number>(2);
  const [refreshCurrentDrawer, setRefreshCurrentDrawer] = useState<number>(0);
  const [isFootprintValue, setIsFootprintValue] = useState<boolean>(false);// 编辑状态下踩点任务是否已创建
  const [allComps, setAllComps] = useState<any[]>([]);
  // const [scrollAccordingTab, setScrollAccordingTab] = useState<boolean>(false); // 是否按照tab点击滚动页面，第一次点击tab后才为true，避免初始化内容后就滚动
  const [clusterHintModalOpen, setClusterHintModalOpen] = useState<boolean>(false);
  const [isAreaProtect, setIsAreaProtect] = useState<boolean>(false);// 区域保护是否存在
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillCount, setPrefillCount] = useState<number | null>(null);
  const [prefillAddressState, setPrefillAddressState] = useState<any>({});
  const [preservePrefilledName, setPreservePrefilledName] = useState(false);
  const prefillAddressActiveRef = useRef(false);

  useEffect(() => {
    // 打开抽屉时重置标记
    open && (saveSucceedRef.current = false);
  }, [open]);

  useEffect(() => {
    if (franchiseeSource) return;
    // 编辑
    if (id) {
      getDetail();
      return;
    }
    // 创建时获取模板详情
    if (templateId) {
      getTempleteDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, templateId, refreshCurrentDrawer, franchiseeSource]);

  useEffect(() => {
    if (!franchiseeSource || !open || id || !templateId || !isYiMing) return;
    let current = true;
    prefillAddressActiveRef.current = true;
    methods.initializeFromFranchisee(() => current);
    return () => {
      current = false;
      prefillAddressActiveRef.current = false;
    };
    // 来源对象在详情或租户切换时由调用方更换，关闭后旧请求不可再写入。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchiseeSource, open, id, templateId, isYiMing, refreshCurrentDrawer]);

  useEffect(() => {
    // 租户信息与模板详情均为异步数据，租户信息后返回时补执行一次一鸣字段计算。
    if (!isYiMing || !formData || !Object.keys(formData).length || !tiledFormDataRef.current.length) return;
    applyYiMingChanceFieldRules({ form, tiledFormData: tiledFormDataRef.current });
  }, [isYiMing, formData]);

  // 一级菜单
  const tabs = useMemo(() => {
    if (!Object.keys(formData).length) return [];
    const { propertyGroupVOList } = formData;
    if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
      return propertyGroupVOList.map((item: any) => ({
        label: item.name,
        key: `${item.id}`, // tabs组件的activeKey是string类型
      }));
    }
    return [];
  }, [formData]);

  /**
   * @description 是否以长列表展示，总字段数<=120时使用
   */
  const isLongList = useMemo(() => {
    const res = isArray(allComps) && allComps.length <= 120;
    return res;
  }, [allComps]);

  const {
    getTempleteDetail,
    getDetail,
    addressComWhenEdit,
    tabChange,
    closeDrawer,
    confirmClose,
    handleFootprintValue,
    computationalLogic,
    ...methods
  } = useMethods({

    /** 加盟商来源在默认值之后应用，旧创建、编辑及 assignedData 路径保持原样。 */
    async initializeFromFranchisee(isCurrent: () => boolean) {
      setPrefillLoading(true);
      setPrefillCount(null);
      setPrefillAddressState({});
      setPreservePrefilledName(false);
      setFormData({});
      form.resetFields();
      try {
        const data = await getTemplateDetail({ id: templateId });
        if (!isCurrent()) return;
        const fields: any[] = [];
        formDataComsToTiled(data?.propertyGroupVOList, fields);
        getEditDefaultValuesByTiledFormData(fields);
        const result = applyFranchiseePrefill(franchiseeSource.propertyGroupVOList, fields, data.supportDirectApproval);
        relevancyShow(fields);
        const applied = result.identifications.filter(identification => fields.some(item => item.identification === identification && item.isShow !== false && isNotEmptyAny(item.textValue)));
        setPreservePrefilledName(applied.includes('basicChancePointName'));
        const hasAddress = applied.includes(addressIdentification);
        if (hasAddress) {
          const resolved = await initializeFranchiseeAddress({
            address: getFranchiseeAddress(franchiseeSource),
            fields,
            form,
            isCurrent: () => isCurrent() && prefillAddressActiveRef.current,
            onBusinessPlan: value => setPrefillAddressState(state => ({ ...state, businessPlanValue: value })),
            onBusinessInfo: value => setPrefillAddressState(state => ({ ...state, businessInfoValue: value })),
          });
          if (!isCurrent()) return;
          data.franchiseeAddressParseFailed = !resolved;
          if (!resolved) V2Message.warning('地址解析失败，请手动选择省市区');
        }
        if (!isCurrent()) return;
        tiledFormDataRef.current = fields;
        setAllComps(fields);
        form.setFieldsValue(getEditValuesByTiledFormData(fields));
        computationalLogic();
        applyYiMingChanceFieldRules({ form, tiledFormData: fields });
        setTabActive(data.propertyGroupVOList?.[0] ? `${data.propertyGroupVOList[0].id}` : '');
        setSupportDirectApproval(data.supportDirectApproval);
        setIsTraditionEvaluation(false);
        setIsFootprintValue(false);
        setPrefillCount(applied.filter(identification => !hasAddress || identification !== pcdIdentification).length);
        setFormData(data);
      } catch {
        if (isCurrent()) V2Message.error('机会点模板加载失败，请关闭后重试');
      } finally {
        if (isCurrent()) setPrefillLoading(false);
      }
    },

    /** 手动选址以后，来源初始化的后台联动结果不再发布。 */
    invalidatePrefillAddress() {
      prefillAddressActiveRef.current = false;
    },

    /** 获取详情 */
    getDetail() {
      if (isLocxxTask && isLocxxOpportunityMockEnabled()) {
        const templateDetail = applyLocxxMockTemplateConfig(createMockLocxxTemplate(id));
        setIsTraditionEvaluation(false);
        formDataComsToTiled(templateDetail.propertyGroupVOList, tiledFormDataRef.current);
        setAllComps(tiledFormDataRef.current);
        setFormData(templateDetail);
        setSupportDirectApproval(templateDetail.supportDirectApproval);
        return;
      }
      getFormDetail({ id, ...(isLocxxTask ? { taskEntry: true } : { approvalTypeValue: drawerData?.evaluationFlowId ? ApprovalTypeValue.ShopEvaluation : ApprovalTypeValue.ChancePointEvaluate }) }).then((res: any) => { // 传了审批类型参数后，返回的每项表单字段access为null，去掉后正常。目前先去掉
        if (isLocxxTask) applyLocxxMockTemplateConfig(res.templateDetailResponse);
        // dynamicRelationId需要特殊赋值
        const { templateDetailResponse, dynamicRelationId, supportDirectApproval, taskId, protectInfo } = res;

        const _isEvaluation = !!taskId; // 是否点位评估
        const _evaluationFlowId = drawerData?.evaluationFlowId; // 是否配置了机会点点位评估审批流
        // 点位评估&未配置点位评估审批流程；Locxx 入口的字段权限改由 DynamicComponent 的 taskEntry 分支独立处理
        setIsTraditionEvaluation(_isEvaluation && !_evaluationFlowId);

        templateDetailResponse.dynamicRelationId = dynamicRelationId;
        const { propertyGroupVOList } = templateDetailResponse;
        // 设置tabs默认值
        if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
          setTabActive(`${propertyGroupVOList[0].id}`);
          tabActiveRef.current = `${propertyGroupVOList[0].id}`;
          formDataComsToTiled(propertyGroupVOList, tiledFormDataRef.current); // 将组件对象平铺到数组中
          // 表单赋值
          const allComs = tiledFormDataRef.current;
          // 这里应该是设置错了，但是目前没接到反馈，怕贸然改动会影响到
          setAllComps(allComps);
          addressComWhenEdit(allComs); // 特殊逻辑，拿到省市区组件的值后，将城市信息塞到地址组件中
          relevancyShow(allComs); // 关联显示
          const values = getEditValuesByTiledFormData(allComs);
          // console.log('values', values);
          // console.log('tiledFormData', JSON.parse(JSON.stringify(allComs)), JSON.parse(JSON.stringify(tiledFormDataRef.current)));
          // setTimeout(() => {
          //   console.log('tiledFormDataRef.current', JSON.parse(JSON.stringify(tiledFormDataRef.current)));
          // }, 1000);
          allComs.forEach((item: any) => {
            const { controlType } = item;
            // // 根据组件类型，将表单数据set到form上（放到 getEditValuesByTiledFormData 内处理）
            // values[identification] = formDataEcho(item);
            // 设置踩点组件数据回显
            handleFootprintValue(item);
            handleJNPValue(item, form);
            if (controlType === ControlType.ESTIMATE_SALES_YHT.value) {
              echoEstimateSalesComYHT(parseValueCatch(item), form);
            } else if (controlType === ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value) {
              echoEstimateSalesConclusionComYHT(parseValueCatch(item), form);
            } else if (controlType === ControlType.PASSENGER_FLOW.value) {
              echoPassengerFlowCom(parseValueCatch(item), form);
            }
          });
          form.setFieldsValue(values);
        }
        setSupportDirectApproval(supportDirectApproval);
        // 查询默认资源项目需要 formDetail 顶层的拓店任务 ID，仅作为表单上下文。
        setFormData({ ...templateDetailResponse, resourceProjectTaskId: taskId });
        setIsAreaProtect(!!protectInfo?.approvalId || (hasAreaProtectionAndAddress(propertyGroupVOList) && drawerData?.antiCollisionApprovalStatus === 1));
      });
    },
    addressComWhenEdit(allComs: any[]) {
      // 编辑时的地址组件的特殊逻辑处理
      const pcdCom = allComs.find((itemCom: any) => itemCom.identification === pcdIdentification);
      const addressCom = allComs.find((itemCom: any) => itemCom.identification === addressIdentification);
      if (!(pcdCom && addressCom)) return;
      const pcdVal = parseValueCatch(pcdCom);
      const addressVal = parseValueCatch(addressCom);
      if (!pcdVal) return;
      if (addressVal) {
        addressCom.textValue = JSON.stringify(Object.assign({}, addressVal, pcdVal));
      }
    },
    getTempleteDetail() {
      if (isLocxxTask && isLocxxOpportunityMockEnabled()) {
        const data = applyLocxxMockTemplateConfig(createMockLocxxTemplate(templateId));
        setIsTraditionEvaluation(false);
        setTabActive(`${data.propertyGroupVOList[0].id}`);
        formDataComsToTiled(data.propertyGroupVOList, tiledFormDataRef.current);
        setAllComps(tiledFormDataRef.current);
        setFormData(data);
        setSupportDirectApproval(data.supportDirectApproval);
        return;
      }
      // 创建时的踩点任务必然是没有的
      setIsFootprintValue(false);
      // 获取模板详情
      getTemplateDetail({ id: templateId, ...(isLocxxTask ? { taskEntry: true, standardTaskId: taskId } : {}) }).then((data: any) => {
        if (isLocxxTask) applyLocxxMockTemplateConfig(data);
        setIsTraditionEvaluation(false);
        const { propertyGroupVOList, supportDirectApproval } = data;
        // 设置tabs默认值
        if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
          setTabActive(`${propertyGroupVOList[0].id}`);
          formDataComsToTiled(propertyGroupVOList, tiledFormDataRef.current); // 将组件对象平铺到数组中
          isArray(assignedData) && assignedData.length && methods.specificAssignedData();
          setAllComps(tiledFormDataRef.current);

          const values = getEditDefaultValuesByTiledFormData(tiledFormDataRef.current);

          relevancyShow(tiledFormDataRef.current); // 关联显示
          form.setFieldsValue(values);
          computationalLogic();
          if (isYiMing) applyYiMingChanceFieldRules({ form, tiledFormData: tiledFormDataRef.current });
        }
        setFormData(data);
        setSupportDirectApproval(supportDirectApproval);
      });
    },
    /** 特殊的赋值逻辑，目前是客流计数创建机会点时自动带入几个字段 */
    specificAssignedData: () => {
      const values: any = {};
      assignedData.forEach((item: any) => {
        tiledFormDataRef.current.forEach((targetItem: any) => {
          if (item.identification === targetItem.identification) {
            targetItem.textValue = item.textValue;
            values[item.identification] = formDataEcho(targetItem);
          }
        });
      });
      form.setFieldsValue(values);
    },
    tabChange(active: string) {
      // setScrollAccordingTab(true);
      setTabActive(active);
      tabActiveRef.current = active;
      const targetEle = tabContentRefs.current?.find((refItem) => refItem?.id === +active);
      // 注意这里的behavior不要设置为smooth，会影响子组件中的handleScroll逻辑
      targetEle?.el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    confirmClose() {
      prefillAddressActiveRef.current = false;
      form.resetFields(); // 表单清空
      closeHandle();
      tiledFormDataRef.current = [];
      setAllComps([]);
    },
    closeDrawer() {
      // 关闭弹窗时
      V2Confirm({
        onSure: () => {
          confirmClose();
          bigdataFn('f9adcbfc-ff89-4ef0-84b0-a067d7307900', '创建机会点详情-取消', '点击了创建机会点详情-取消', { op_block: '创建机会点', op_event: ``, });
        },
        zIndex: 1010,
        title: '操作提示',
        content: '退出后将不保存当前操作，请确认是否退出。',
      });
    },
    /** 处理踩点组件回显 */
    handleFootprintValue(item) {
      // locationFootprint 后端返回的踩点组件唯一标识
      if (item.identification === 'locationFootprint') {

        const textValue = JSON.parse(item?.textValue || null);
        if (!isNotEmptyAny(textValue)) return;
        // 判断是否已经有踩点任务
        setIsFootprintValue(textValue.dynamicComponent_footprint_isStart === radioEnum.AGREE);

        const values = {};
        // 数据回显
        values['dynamicComponent_footprint_isStart'] = textValue.dynamicComponent_footprint_isStart;
        form.setFieldsValue(values);
        // 如果踩点任务未创建，就不往下进行
        if (textValue.dynamicComponent_footprint_isStart === radioEnum.DENY) {
          return;
        }
        // 回显踩点日期规则
        values['dynamicComponent_footprint_checkRule'] = textValue?.dynamicComponent_footprint_checkRule;
        // 回显踩点时间
        values['dynamicComponent_footprint_checkPeriod'] =
        [
          moment(textValue?.dynamicComponent_footprint_checkPeriod[0], 'HH:mm'),
          moment(textValue?.dynamicComponent_footprint_checkPeriod[1], 'HH:mm'),
        ];
        form.setFieldsValue(values);

      }
    },
    /** 计算公式的逻辑 */
    computationalLogic () {
      // 查找配置了计算公式的组件进行计算
      const calculateComs = getCalculateComs(tiledFormDataRef.current);
      // 所有的数字输入框组件
      const numInputComs = tiledFormDataRef.current.filter((item: any) => item.controlType === ControlType.INPUT_NUMBER.value);
      // 特殊控件字段：加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const specialCOms = tiledFormDataRef.current.filter((item: any) => item.identification === 'franchiseFeeYHT');
      // 计算对象：所有的数字输入框组件+加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const objCalculateComs = [...numInputComs, ...specialCOms];
      calculateHandle(objCalculateComs, calculateComs);
      // 计算的时候只设置了对应的textValue，还需要赋值到Form上
      numInputComs.forEach((item: any) => {
        const { identification, textValue } = item;
        const value = textValue ? JSON.parse(textValue)?.value : ''; // 当前组件的值
        form.setFieldValue(identification, value);
      });
    },
  });

  return (
    <div>
      <V2Drawer
        open={open}
        onClose={() => closeDrawer()}
        destroyOnClose
        className={cs('dynamicComponent', styles.formDrawer)}
        contentWrapperStyle={{
          width: '76%',
          minWidth: '1100px',
          maxWidth: 'auto',
        }}
      >
        <V2Container
          // 24px上padding
          // style={{ height: 'calc(100vh - 24px)' }}
          style={{ height: '100vh' }}
          extraContent={{
            top: (
              <div ref={topRef}>
                <Header
                  id={id}
                  chancepointAlias={chancepointAlias}
                  importPermission={importPermission}
                  onSearch={onSearch}
                  setRefreshCurrentDrawer={setRefreshCurrentDrawer}
                />
                {franchiseeSource && prefillCount !== null && <Alert
                  className={styles.prefillHint}
                  type='info'
                  showIcon
                  message={`已自动带入加盟商填写的 ${prefillCount} 个字段，请核对补充`}
                />}
                <V2Tabs
                  items={tabs}
                  activeKey={tabActive}
                  onChange={tabChange}
                  className={styles.tabsCon}
                />
              </div>
            ),
            bottom: (
              (!franchiseeSource || (!prefillLoading && prefillCount !== null)) && <Footer
                id={id}
                taskId={taskId}
                drawerData={drawerData}
                assignedData={assignedData}
                franchiseeId={franchiseeSource?.id}
                form={form}
                formData={formData}
                isExpandStoreTaskIn={isExpandStoreTaskIn}
                isLocxxTask={isLocxxTask}
                handleLocxxSave={handleLocxxSave}
                needAssiocateChance={needAssiocateChance}
                supportDirectApproval={supportDirectApproval}
                isFootprintValue={isFootprintValue}
                saveSucceedRef={saveSucceedRef}
                tiledFormDataRef={tiledFormDataRef}
                setClusterHintModalOpen={setClusterHintModalOpen}
                handleExpandStoreApproved={handleExpandStoreApproved}
                handleCreateSuccessed={handleCreateSuccessed}
                handleEditSuccessed={handleEditSuccessed}
                onSearch={onSearch}
                closeDrawer={closeDrawer}
                confirmClose={confirmClose}
              />
            ),
          }}
        >
          <Main
            form={form}
            formData={resourceProjectFormData}
            isTraditionEvaluation={isTraditionEvaluation}
            supportDirectApproval={supportDirectApproval}
            setFormData={setFormData}
            topRef={topRef}
            tabActiveRef={tabActiveRef}
            tabContentRefs={tabContentRefs}
            tabs={tabs}
            active={tabActive}
            setTabActive={setTabActive}
            // scrollAccordingTab={scrollAccordingTab}
            isLongList={isLongList}
            tiledFormDataRef={tiledFormDataRef}
            isFootprintValue={isFootprintValue}
            isAreaProtect={isAreaProtect}
            computationalLogic={computationalLogic}
            expansionTaskClusterId={expansionTaskClusterId}
            isEdit={!!id}
            franchiseePrefill={!!franchiseeSource}
            prefillLoading={prefillLoading}
            preservePrefilledName={preservePrefilledName}
            prefillAddressState={prefillAddressState}
            onManualAddressChange={methods.invalidatePrefillAddress}
          />
        </V2Container>
      </V2Drawer>

      {/* 是否在所属商圈中的提示弹窗 */}
      <ClusterHintModal
        clusterHintModalOpen={clusterHintModalOpen}
        setClusterHintModalOpen={setClusterHintModalOpen}
        // form={form}
        confirmClose={confirmClose}
        tiledFormDataRef={tiledFormDataRef}
        formData={formData}
      />
    </div>
  );
};

export default ChanceCreateDrawer;
